import React, { useState, useEffect, useRef } from 'react';
import './login.scss';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

export default function LoginLayout({ username, password, remember, error, loading, onChange, onSubmit, onGoogleLogin, onForgotPassword, trackBack }) {
  const [showPassword, setShowPassword] = useState(false);
  const [googleError, setGoogleError] = useState('');
  const googleBtnRef = useRef(null);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !onGoogleLogin) return;

    function renderBtn() {
      if (!window.google || !googleBtnRef.current) return;
      const w = googleBtnRef.current.getBoundingClientRect().width || 300;
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: Math.floor(w),
      });
    }

    function initGoogle() {
      if (!window.google || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (resp) => {
          setGoogleError('');
          try {
            await onGoogleLogin(resp.credential);
          } catch (err) {
            setGoogleError(err?.response?.data?.message || err?.message || 'Google sign-in failed. Please try again.');
          }
        },
        auto_select: false,
      });
      // Wait one frame so the container has its final painted width
      requestAnimationFrame(renderBtn);
    }

    // Re-render button whenever the container is resized (handles responsive layout changes)
    let ro;
    if (typeof ResizeObserver !== 'undefined' && googleBtnRef.current) {
      ro = new ResizeObserver(() => { if (window.google) renderBtn(); });
      ro.observe(googleBtnRef.current);
    }

    if (typeof window.google !== 'undefined') {
      initGoogle();
      return () => ro && ro.disconnect();
    }

    const existing = document.getElementById('google-gsi-script');
    if (existing) {
      existing.addEventListener('load', initGoogle);
      return () => {
        existing.removeEventListener('load', initGoogle);
        ro && ro.disconnect();
      };
    }

    const script = document.createElement('script');
    script.id = 'google-gsi-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = initGoogle;
    document.head.appendChild(script);
    return () => ro && ro.disconnect();
  }, [onGoogleLogin]);

  return (
    <div className="v3-wrapper">
      <div className="v3-card">

        {/* ── LEFT PANEL ── */}
        <div className="v3-left">
          {/* diagonal green accent */}
          <div className="v3-left__accent" />

          <div className="v3-left__content">
            {/* Logo */}
            <div className="v3-logo">
              <img
                src={`${process.env.PUBLIC_URL}/images/fmzlogo.png`}
                alt="FMZ Net Solutions"
              />
            </div>

            {/* Headline */}
            <h1 className="v3-headline">
              SMART SOLUTIONS<br />
              <span>CONNECTED FUTURE</span>
            </h1>
            <div className="v3-divider" />

            {/* Description */}
            <p className="v3-desc">
              FMZ Solutions delivers innovative, reliable
              and scalable solutions to empower your
              business in a digital world.
            </p>

            {/* Feature icons */}
            <div className="v3-features">
              <div className="v3-feature">
                <span className="v3-feature__icon">
                  {/* connectivity */}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M12 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/>
                    <path d="M5.6 14.4a9 9 0 0 1 12.8 0"/>
                    <path d="M2 10.8a14 14 0 0 1 20 0"/>
                  </svg>
                </span>
                <span className="v3-feature__label">CONNECTIVITY</span>
                <span className="v3-feature__sub">Reliable &amp; Fast</span>
              </div>

              <div className="v3-feature">
                <span className="v3-feature__icon">
                  {/* shield check */}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V7L12 2z"/>
                    <polyline points="9 12 11 14 15 10"/>
                  </svg>
                </span>
                <span className="v3-feature__label">SECURITY</span>
                <span className="v3-feature__sub">Trusted &amp; Secure</span>
              </div>

              <div className="v3-feature">
                <span className="v3-feature__icon">
                  {/* gear */}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                  </svg>
                </span>
                <span className="v3-feature__label">SOLUTIONS</span>
                <span className="v3-feature__sub">Smart &amp; Scalable</span>
              </div>

              <div className="v3-feature">
                <span className="v3-feature__icon">
                  {/* bar chart */}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <line x1="18" y1="20" x2="18" y2="10"/>
                    <line x1="12" y1="20" x2="12" y2="4"/>
                    <line x1="6"  y1="20" x2="6"  y2="14"/>
                    <polyline points="2 20 22 20"/>
                    <polyline points="14 6 12 4 10 6"/>
                  </svg>
                </span>
                <span className="v3-feature__label">PERFORMANCE</span>
                <span className="v3-feature__sub">Results Driven</span>
              </div>
            </div>
          </div>

          {/* City skyline illustration */}
          <div className="v3-cityscape">
            <svg viewBox="0 0 800 220" preserveAspectRatio="xMidYMax meet" xmlns="http://www.w3.org/2000/svg">
              {/* buildings */}
              <rect x="0"   y="120" width="40"  height="100" fill="#0d3a4a" opacity=".9"/>
              <rect x="5"   y="90"  width="28"  height="30"  fill="#0d3a4a" opacity=".9"/>
              <rect x="45"  y="100" width="50"  height="120" fill="#0a2e3b" opacity=".9"/>
              <rect x="55"  y="70"  width="30"  height="32"  fill="#0a2e3b" opacity=".9"/>
              <rect x="100" y="80"  width="35"  height="140" fill="#0d3a4a" opacity=".9"/>
              <rect x="140" y="110" width="55"  height="110" fill="#092939" opacity=".9"/>
              <rect x="150" y="60"  width="35"  height="52"  fill="#092939" opacity=".9"/>
              <rect x="200" y="90"  width="40"  height="130" fill="#0d3a4a" opacity=".9"/>
              <rect x="245" y="130" width="60"  height="90"  fill="#0a2e3b" opacity=".9"/>
              <rect x="250" y="85"  width="40"  height="47"  fill="#0a2e3b" opacity=".9"/>
              <rect x="310" y="70"  width="45"  height="150" fill="#0d3a4a" opacity=".9"/>
              <rect x="360" y="100" width="50"  height="120" fill="#092939" opacity=".9"/>
              <rect x="415" y="85"  width="40"  height="135" fill="#0d3a4a" opacity=".9"/>
              <rect x="460" y="115" width="55"  height="105" fill="#0a2e3b" opacity=".9"/>
              <rect x="465" y="75"  width="35"  height="42"  fill="#0a2e3b" opacity=".9"/>
              <rect x="520" y="90"  width="40"  height="130" fill="#0d3a4a" opacity=".9"/>
              <rect x="565" y="120" width="50"  height="100" fill="#092939" opacity=".9"/>
              <rect x="620" y="80"  width="45"  height="140" fill="#0d3a4a" opacity=".9"/>
              <rect x="670" y="105" width="55"  height="115" fill="#0a2e3b" opacity=".9"/>
              <rect x="730" y="95"  width="40"  height="125" fill="#0d3a4a" opacity=".9"/>
              <rect x="775" y="130" width="25"  height="90"  fill="#092939" opacity=".9"/>

              {/* windows */}
              {[10,18,10,18,10,18].map((x,i) => (
                <rect key={i} x={x} y={95+i*8} width="6" height="4" fill="#8bc34a" opacity=".6"/>
              ))}
              {[108,120,108,120].map((x,i) => (
                <rect key={i} x={x} y={88+i*10} width="6" height="5" fill="#8bc34a" opacity=".5"/>
              ))}
              {[318,332,318,332,318].map((x,i) => (
                <rect key={i} x={x} y={78+i*12} width="6" height="5" fill="#8bc34a" opacity=".6"/>
              ))}

              {/* network arcs */}
              <g stroke="#8bc34a" strokeWidth="1.2" fill="none" opacity=".55">
                <path d="M80 180 Q200 80 320 160"/>
                <path d="M200 160 Q350 40 500 150"/>
                <path d="M320 160 Q480 60 640 155"/>
                <path d="M500 150 Q620 70 750 160"/>
                <path d="M80  180 Q400 20 720 175"/>
              </g>

              {/* network nodes */}
              <g fill="#8bc34a" opacity=".8">
                <circle cx="80"  cy="180" r="4"/>
                <circle cx="200" cy="160" r="3"/>
                <circle cx="320" cy="160" r="4"/>
                <circle cx="500" cy="150" r="3"/>
                <circle cx="640" cy="155" r="4"/>
                <circle cx="750" cy="160" r="3"/>
              </g>
            </svg>
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="v3-right">
          <div className="v3-form-inner">
            <h2 className="v3-title">Welcome Back</h2>
            <p className="v3-subtitle">Sign in to your FMZ Net Solutions account</p>

            {error && <div className="v3-error">{error}</div>}

            <form onSubmit={onSubmit} noValidate>

              {/* Username */}
              <div className="v3-field">
                <label>Email Address</label>
                <div className="v3-input-wrap">
                  <span className="v3-input-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                  </span>
                  <input
                    type="text"
                    placeholder="Enter your email"
                    autoComplete="username"
                    value={username}
                    onChange={e => onChange('username', e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div className="v3-field">
                <label>Password</label>
                <div className="v3-input-wrap">
                  <span className="v3-input-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                  </span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    value={password}
                    onChange={e => onChange('password', e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="v3-toggle-pw"
                    onClick={() => setShowPassword(v => !v)}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      /* eye-off */
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      /* eye */
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Remember me + Forgot */}
              <div className="v3-row-options">
                <label className="v3-remember">
                  <input
                    type="checkbox"
                    checked={!!remember}
                    onChange={e => onChange('remember', e.target.checked)}
                  /> Remember me
                </label>
                <span className="v3-forgot" onClick={onForgotPassword}>Forgot Password?</span>
              </div>

              {/* Sign In */}
              <button className="v3-btn-signin" type="submit" disabled={loading}>
                {loading ? 'Signing in…' : 'Sign In'}
              </button>

              {/* Divider */}
              <div className="v3-or">
                <span />
                <p>or</p>
                <span />
              </div>

              {/* Google Sign-In */}
              {GOOGLE_CLIENT_ID ? (
                <>
                  <div ref={googleBtnRef} className="v3-google-btn-wrap" />
                  {googleError && (
                    <div style={{ marginTop: 10, padding: '9px 12px', background: '#fdecea', border: '1px solid #f5c6cb', borderRadius: 8, fontSize: 13, color: '#c0392b', lineHeight: 1.4 }}>
                      {googleError}
                    </div>
                  )}
                </>
              ) : (
                <button className="v3-btn-sso" type="button" disabled style={{ opacity: 0.45, cursor: 'not-allowed' }}>
                  <svg viewBox="0 0 24 24" width="18" height="18" style={{ marginRight: 8 }}>
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Sign in with Google
                </button>
              )}
            </form>
          </div>

          <p className="v3-footer">© 2024 FMZ Net Solutions. All rights reserved.</p>

          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <a
              href={trackBack || '/track'}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 18px', background: 'transparent', border: '1.5px solid #8bc34a', borderRadius: 8, color: '#8bc34a', fontSize: 13, fontWeight: 600, textDecoration: 'none', cursor: 'pointer', transition: 'background 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(139,195,74,0.08)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {trackBack ? (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><polyline points="15 18 9 12 15 6"/></svg>
                  Back to Tracking
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  FMZ Repair Tracking
                </>
              )}
            </a>
          </div>
        </div>

      </div>
    </div>
  );
}
